/**
 * EffectSlot Component
 * 
 * Individual slot in the effects chain for adding, configuring, and managing effects.
 * Handles both empty and filled states with appropriate UI for each.
 */

import { useState, useCallback, useMemo } from 'react';
import type { EffectInstance, EffectType, EffectDefinition } from '../../services/effects/EffectsManager';
import { EffectSelectorModal } from './EffectSelectorModal';
import { RotaryKnob } from '../ui/RotaryKnob';
import { EQVisualizer } from './effects/EQVisualizer';
import { CompressorControls } from './effects/CompressorControls';
import { FlangerControls } from './effects/FlangerControls';
import { TremoloControls } from './effects/TremoloControls';
import { PingPongDelayControls } from './effects/PingPongDelayControls';
import { AutoWahControls } from './effects/AutoWahControls';
import { getParameterDescription } from '../../constants/effectDescriptions';
import './EffectSlot.css';

/**
 * Props for EffectSlot component
 */
export interface EffectSlotProps {
  /** Row index */
  row: number;
  /** Slot index (0-2) */
  slot: number;
  /** Current effect in this slot (null if empty) */
  effect: EffectInstance | null;
  /** Available effect definitions */
  availableEffects: EffectDefinition[];
  /** Callback when an effect is selected for this slot */
  onEffectSelect: (type: EffectType) => void;
  /** Callback when a parameter value changes */
  onParameterChange: (parameterId: string, value: number) => void;
  /** Callback when multiple parameters change at once (for presets) */
  onParametersChange?: (parameters: Record<string, number>) => void;
  /** Callback when bypass state changes */
  onBypass: (bypassed: boolean) => void;
  /** Callback when effect is removed */
  onRemove: () => void;
}

/**
 * EffectSlot component
 * 
 * Renders an individual effect slot with:
 * - Empty state: Shows "Add Effect" button with dropdown selector
 * - Filled state: Shows effect name, parameter controls, bypass and remove buttons
 * 
 * @param props - Component props
 * @returns EffectSlot component
 */
export function EffectSlot({
  row,
  slot,
  effect,
  availableEffects,
  onEffectSelect,
  onParameterChange,
  onParametersChange,
  onBypass,
  onRemove,
}: EffectSlotProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Get effect definition for the current effect
   */
  const effectDefinition = useMemo(() => {
    if (!effect) return null;
    return availableEffects.find(def => def.type === effect.type) || null;
  }, [effect, availableEffects]);

  /**
   * Handle effect selection from modal
   */
  const handleEffectSelect = useCallback((type: EffectType) => {
    onEffectSelect(type);
    setIsModalOpen(false);
  }, [onEffectSelect]);

  /**
   * Open modal
   */
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  // Render empty slot
  if (!effect) {
    return (
      <>
        <div className="effect-slot effect-slot-empty">
          <div className="effect-slot-empty-content">
            <button
              className="effect-slot-add-btn"
              onClick={openModal}
              aria-label={`Add effect to slot ${slot + 1} on row ${row + 1}`}
              type="button"
            >
              <i className="fas fa-plus" aria-hidden="true"></i>
              <span>Add Effect</span>
            </button>
          </div>
        </div>
        
        {/* Effect selector modal */}
        <EffectSelectorModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          effects={availableEffects}
          onSelect={handleEffectSelect}
          slot={slot}
          row={row}
        />
      </>
    );
  }

  // Render filled slot with effect controls
  return (
    <div className={`effect-slot effect-slot-filled ${effect.type === 'eq' ? 'effect-slot-eq' : ''} ${effect.bypassed ? 'effect-slot-bypassed' : ''}`}>
      {/* Effect header with name and remove button */}
      <div className="effect-slot-header">
        <span className="effect-slot-name">
          {effectDefinition?.name || effect.type}
        </span>
        <button
          className="effect-slot-remove-btn"
          onClick={onRemove}
          aria-label={`Remove ${effectDefinition?.name || effect.type} effect from slot ${slot + 1}`}
          type="button"
        >
          <i className="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>

      {/* Effect parameter controls */}
      <div className={`effect-slot-controls ${
        effect.type === 'eq' ? 'effect-slot-controls-eq' : 
        ['compressor', 'flanger', 'tremolo', 'pingpong', 'autowah'].includes(effect.type) ? 'effect-slot-controls-specialized' :
        (effectDefinition?.parameters.length === 4 ? 'effect-slot-controls-grid' : '')
      }`}>
        {effect.type === 'compressor' && effectDefinition ? (
          // Specialized layout for Compressor
          <CompressorControls
            effect={effect}
            effectDefinition={effectDefinition}
            onParameterChange={onParameterChange}
            onParametersChange={onParametersChange}
            disabled={effect.bypassed}
          />
        ) : effect.type === 'flanger' && effectDefinition ? (
          // Specialized layout for Flanger
          <FlangerControls
            effect={effect}
            effectDefinition={effectDefinition}
            onParameterChange={onParameterChange}
            onParametersChange={onParametersChange}
            disabled={effect.bypassed}
          />
        ) : effect.type === 'tremolo' && effectDefinition ? (
          // Specialized layout for Tremolo
          <TremoloControls
            effect={effect}
            effectDefinition={effectDefinition}
            onParameterChange={onParameterChange}
            onParametersChange={onParametersChange}
            disabled={effect.bypassed}
          />
        ) : effect.type === 'pingpong' && effectDefinition ? (
          // Specialized layout for Ping-Pong Delay
          <PingPongDelayControls
            effect={effect}
            effectDefinition={effectDefinition}
            onParameterChange={onParameterChange}
            onParametersChange={onParametersChange}
            disabled={effect.bypassed}
          />
        ) : effect.type === 'autowah' && effectDefinition ? (
          // Specialized layout for Auto-Wah
          <AutoWahControls
            effect={effect}
            effectDefinition={effectDefinition}
            onParameterChange={onParameterChange}
            onParametersChange={onParametersChange}
            disabled={effect.bypassed}
          />
        ) : effect.type === 'eq' && effectDefinition?.parameters ? (
          // Special layout for EQ: group by band
          <>
            <div className="eq-band">
              <div className="eq-band-title">Low</div>
              <div className="eq-band-controls">
                {effectDefinition.parameters
                  .filter(p => p.id.startsWith('low'))
                  .map(paramDef => {
                    const paramValue = effect.parameters[paramDef.id] ?? paramDef.default;
                    const tooltip = getParameterDescription(effect.type, paramDef.id);
                    return (
                      <RotaryKnob
                        key={paramDef.id}
                        value={paramValue}
                        min={paramDef.min}
                        max={paramDef.max}
                        default={paramDef.default}
                        step={paramDef.step}
                        label={paramDef.name.replace('Low ', '')}
                        {...(paramDef.unit && { unit: paramDef.unit })}
                        onChange={(value) => onParameterChange(paramDef.id, value)}
                        disabled={effect.bypassed}
                        size="medium"
                        {...(tooltip && { tooltip })}
                      />
                    );
                  })}
              </div>
            </div>
            <div className="eq-band">
              <div className="eq-band-title">Mid</div>
              <div className="eq-band-controls">
                {effectDefinition.parameters
                  .filter(p => p.id.startsWith('mid'))
                  .map(paramDef => {
                    const paramValue = effect.parameters[paramDef.id] ?? paramDef.default;
                    const tooltip = getParameterDescription(effect.type, paramDef.id);
                    return (
                      <RotaryKnob
                        key={paramDef.id}
                        value={paramValue}
                        min={paramDef.min}
                        max={paramDef.max}
                        default={paramDef.default}
                        step={paramDef.step}
                        label={paramDef.name.replace('Mid ', '')}
                        {...(paramDef.unit && { unit: paramDef.unit })}
                        onChange={(value) => onParameterChange(paramDef.id, value)}
                        disabled={effect.bypassed}
                        size="medium"
                        {...(tooltip && { tooltip })}
                      />
                    );
                  })}
              </div>
            </div>
            <div className="eq-band">
              <div className="eq-band-title">High</div>
              <div className="eq-band-controls">
                {effectDefinition.parameters
                  .filter(p => p.id.startsWith('high'))
                  .map(paramDef => {
                    const paramValue = effect.parameters[paramDef.id] ?? paramDef.default;
                    const tooltip = getParameterDescription(effect.type, paramDef.id);
                    return (
                      <RotaryKnob
                        key={paramDef.id}
                        value={paramValue}
                        min={paramDef.min}
                        max={paramDef.max}
                        default={paramDef.default}
                        step={paramDef.step}
                        label={paramDef.name.replace('High ', '')}
                        {...(paramDef.unit && { unit: paramDef.unit })}
                        onChange={(value) => onParameterChange(paramDef.id, value)}
                        disabled={effect.bypassed}
                        size="medium"
                        {...(tooltip && { tooltip })}
                      />
                    );
                  })}
              </div>
            </div>
            
            {/* EQ Frequency Response Visualizer */}
            <div className="eq-visualizer-container">
              <EQVisualizer
                lowFreq={effect.parameters['lowFreq'] ?? 100}
                lowGain={effect.parameters['lowGain'] ?? 0}
                lowQ={effect.parameters['lowQ'] ?? 1.0}
                midFreq={effect.parameters['midFreq'] ?? 1000}
                midGain={effect.parameters['midGain'] ?? 0}
                midQ={effect.parameters['midQ'] ?? 1.0}
                highFreq={effect.parameters['highFreq'] ?? 8000}
                highGain={effect.parameters['highGain'] ?? 0}
                highQ={effect.parameters['highQ'] ?? 1.0}
              />
            </div>
          </>
        ) : (
          // Default layout for other effects
          <>
            {effectDefinition?.parameters.map(paramDef => {
              const paramValue = effect.parameters[paramDef.id] ?? paramDef.default;
              const tooltip = getParameterDescription(effect.type, paramDef.id);
              
              return (
                <RotaryKnob
                  key={paramDef.id}
                  value={paramValue}
                  min={paramDef.min}
                  max={paramDef.max}
                  default={paramDef.default}
                  step={paramDef.step}
                  label={paramDef.name}
                  {...(paramDef.unit && { unit: paramDef.unit })}
                  onChange={(value) => onParameterChange(paramDef.id, value)}
                  disabled={effect.bypassed}
                  size="medium"
                  {...(tooltip && { tooltip })}
                />
              );
            })}
            
            {/* Show message if no parameters */}
            {(!effectDefinition?.parameters || effectDefinition.parameters.length === 0) && (
              <div className="effect-slot-no-params">
                <span>No parameters available</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bypass and remove buttons - will be implemented in subtask 8.4 */}
      <div className="effect-slot-actions">
        <button
          className={`effect-slot-bypass-btn ${effect.bypassed ? 'bypassed' : ''}`}
          onClick={() => onBypass(!effect.bypassed)}
          aria-label={`${effect.bypassed ? 'Enable' : 'Bypass'} ${effect.type} effect`}
          type="button"
        >
          <i className={`fas fa-power-off ${effect.bypassed ? 'icon-bypassed' : ''}`} aria-hidden="true"></i>
          <span>{effect.bypassed ? 'Bypassed' : 'Active'}</span>
        </button>
      </div>
    </div>
  );
}
